import React, { useState, useEffect, useRef } from 'react';
import {
    Typography,
    Card,
    Table,
    Button,
    Space,
    Input,
    Select,
    Modal,
    Form,
    DatePicker,
    Divider,
    InputNumber,
    message,
    Popconfirm,
    Tag,
    Tooltip,
    Row,
    Col,
    Statistic,
    Drawer,
    Descriptions,
    Tabs,
    Empty,
    List,
    FloatButton,
    Grid,
    Radio,
    Alert,
    Checkbox,
    Spin
} from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    TruckOutlined,
    DollarOutlined,
    CalendarOutlined,
    UserOutlined,
    PrinterOutlined,
    FilterOutlined,
    CalculatorOutlined,

} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getDeliveryNotes, getDeliveryNoteById, createDeliveryNote, updateDeliveryNote, deleteDeliveryNote, convertQuoteToDeliveryNote } from '../../services/deliveryNoteService';
import { getContacts, getCustomersForDropdown } from '../../services/contactService';
import { getProducts } from '../../services/productService';
import { generateDeliveryNotePDF } from '../../services/deliveryNotePdfService';
import SignatureLayoutControls from '../../components/SignatureLayoutControls';
import useDocumentSignatureLayout from '../../hooks/useDocumentSignatureLayout';
import {
    getCompanySettings,
    updateCompanySettings,
    uploadCompanyLogo,
    deleteCompanyLogo,
    getLogoUrl
} from '../../services/settingsService';
import UnitsSelect from '../../components/UnitsSelect';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

const DeliveryNotesPage = () => {
    const [DeliveryNoteDetailInfo, setDeliveryNotesDetailInfo] = useState({});
    const [deliveryNotes, setDeliveryNotes] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [editingDeliveryNote, setEditingDeliveryNote] = useState(null);
    const [selectedDeliveryNote, setSelectedDeliveryNote] = useState(null);
    const [form] = Form.useForm();

    const [pdfModalVisible, setPdfModalVisible] = useState(false);
    const [pdfDataUrl, setPdfDataUrl] = useState('');
    const [pdfPreviewDeliveryNote, setPdfPreviewDeliveryNote] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [showPdfTotals, setShowPdfTotals] = useState(false);

    const [company, setCompany] = useState(null);
    const [logoUrl, setLogoUrl] = useState(null);
    const {
        signatureLayout,
        setSignatureLayout,
        signatureLayoutSaving,
        saveSignatureLayout
    } = useDocumentSignatureLayout('delivery_note', company, setCompany);

    useEffect(() => {
        if (!pdfModalVisible || !pdfPreviewDeliveryNote) return undefined;

        let active = true;
        setPdfLoading(true);
        setPdfDataUrl('');
        generateDeliveryNotePDF(
            pdfPreviewDeliveryNote,
            company || {},
            'include',
            undefined,
            signatureLayout,
            { showTotals: showPdfTotals }
        )
            .then(dataUrl => {
                if (active) setPdfDataUrl(dataUrl);
            })
            .catch(error => {
                console.error('Delivery note PDF preview error:', error);
                if (active) message.error('ไม่สามารถสร้างตัวอย่างใบส่งของชั่วคราวได้: ' + error.message);
            })
            .finally(() => {
                if (active) setPdfLoading(false);
            });

        return () => { active = false; };
    }, [pdfModalVisible, pdfPreviewDeliveryNote, company, signatureLayout, showPdfTotals]);

    const handleSaveSignatureLayout = async () => {
        try {
            await saveSignatureLayout();
            message.success('บันทึกตำแหน่งลายเซ็นสำหรับใบส่งของชั่วคราวแล้ว');
        } catch (error) {
            message.error(error.response?.data?.message || 'ไม่สามารถบันทึกตำแหน่งลายเซ็นได้');
        }
    };

    // Responsive state
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);

    // Pagination and filters
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        contact_id: '',
    });
    const [statusCounts, setStatusCounts] = useState([]);

    // Status options and colors
    const statusOptions = [
        { value: 'draft', label: 'ร่าง', color: 'default' },
        { value: 'sent', label: 'ส่งแล้ว', color: 'processing' },
        { value: 'received', label: 'ลูกค้ารับแล้ว', color: 'success' },
        { value: 'cancelled', label: 'ยกเลิก', color: 'error' },
    ];

    const getStatusColor = (status) => {
        const statusConfig = statusOptions.find(opt => opt.value === status);
        return statusConfig?.color || 'default';
    };

    const getStatusLabel = (status) => {
        const statusConfig = statusOptions.find(opt => opt.value === status);
        return statusConfig?.label || status;
    };

    // Custom filter function for contact selection
    const customContactFilter = (input, option) => {
        if (!input) return true;

        let searchText = '';

        if (typeof option.children === 'string') {
            searchText = option.children;
        } else if (React.isValidElement(option.children)) {
            searchText = option.children.props?.children || '';
        } else if (Array.isArray(option.children)) {
            searchText = option.children.map(child =>
                typeof child === 'string' ? child : ''
            ).join(' ');
        }

        if (option.label && typeof option.label === 'string') {
            searchText += ' ' + option.label;
        }

        return searchText.toLowerCase().includes(input.toLowerCase());
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await getCompanySettings();
            if (response.data.success) {
                const data = response.data.data;
                setCompany(data);
                form.setFieldsValue(data);
                setLogoUrl(data.logo_path ? getLogoUrl(`${data.logo_path}`) : null);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            message.error('ไม่สามารถโหลดข้อมูลบริษัทได้');
        } finally {
            setLoading(false);
        }
    };

    // Load data
    useEffect(() => {
        loadDeliveryNotes();
        loadContacts();
        loadProducts();
    }, [pagination.current, pagination.pageSize, filters]);

    const loadDeliveryNotes = async () => {
        try {
            setLoading(true);
            const response = await getDeliveryNotes(
                pagination.current,
                pagination.pageSize,
                filters.search,
                filters.status,
                filters.contact_id
            );

            if (response.success) {
                setDeliveryNotes(response.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.pagination.total,
                }));
                setStatusCounts(response.statusCounts || []);
            }
        } catch (error) {
            console.error('Error loading delivery notes:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadContacts = async () => {
        try {
            const response = await getCustomersForDropdown();
            if (response.success) {
                setContacts(response.data);
            }
        } catch (error) {
            console.error('Error loading contacts:', error);
        }
    };

    const loadProducts = async () => {
        try {
            const response = await getProducts(1, 1000);
            if (response.success) {
                setProducts(response.data);
            }
        } catch (error) {
            console.error('Error loading products:', error);
        }
    };

    // Handle table changes
    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    // Handle search
    const handleSearch = (value) => {
        setFilters(prev => ({ ...prev, search: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Clear filters
    const clearFilters = () => {
        setFilters({ search: '', status: '', contact_id: '' });
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Handle modal operations
    const showCreateModal = () => {
        setEditingDeliveryNote(null);
        setModalVisible(true);
        form.resetFields();
        form.setFieldsValue({
            document_date: dayjs(),
            delivery_date: dayjs(),
            expected_delivery_date: dayjs().add(7, 'day'),
            tax_rate: 7,
            discount: 0,
            vat_mode: 'include',
            items: [{ description: '', quantity: 1, unit_price: 0, unit: 'ชิ้น', is_free: false }]
        });
    };

    const showEditModal = async (deliveryNote) => {
        try {
            const response = await getDeliveryNoteById(deliveryNote.id);
            if (response.success) {
                setEditingDeliveryNote(response.data);
                setDeliveryNotesDetailInfo(response.data);
                setModalVisible(true);
                form.setFieldsValue({
                    contact_id: response.data.contact_id,
                    document_date: dayjs(response.data.document_date),
                    delivery_date: dayjs(response.data.delivery_date),
                    expected_delivery_date: response.data.expected_delivery_date ? dayjs(response.data.expected_delivery_date) : null,
                    tax_rate: response.data.tax_rate,
                    discount: response.data.discount,
                    vat_mode: response.data.vat_mode || 'include',
                    total_amount: response.data.total_amount,
                    tax_amount: response.data.tax_amount,
                    subtotal: response.data.subtotal,
                    notes: response.data.notes,
                    items: response.data.items?.map(item => ({
                        ...item,
                        unit: item.unit || 'ชิ้น',
                        is_free: item.is_free || false
                    })) || [{ description: '', quantity: 1, unit_price: 0, unit: 'ชิ้น', is_free: false }]
                });
            }
        } catch (error) {
            message.error('เกิดข้อผิดพลาดในการโหลดข้อมูลใบส่งของ');
        }
    };

    const showDetailDrawer = async (deliveryNote) => {
        try {
            setLoading(true);
            const response = await getDeliveryNoteById(deliveryNote.id);
            if (response.success) {
                setSelectedDeliveryNote(response.data);
                setDetailDrawerVisible(true);
            }
        } catch (error) {
            message.error('เกิดข้อผิดพลาดในการโหลดรายละเอียดใบส่งของ');
        } finally {
            setLoading(false);
        }
    };

    // Handle form submission
    const handleSubmit = async (values) => {
        try {
            setLoading(true);

            const formData = {
                ...values,
                document_date: values.document_date.format('YYYY-MM-DD'),
                delivery_date: values.delivery_date.format('YYYY-MM-DD'),
                expected_delivery_date: values.expected_delivery_date ? values.expected_delivery_date.format('YYYY-MM-DD') : null,
                vat_mode: values.vat_mode || 'include',
            };

            if (editingDeliveryNote) {
                await updateDeliveryNote(editingDeliveryNote.id, formData);
                message.success('อัปเดตใบส่งของเรียบร้อยแล้ว');
            } else {
                await createDeliveryNote(formData);
                message.success('สร้างใบส่งของเรียบร้อยแล้ว');
            }

            setModalVisible(false);
            loadDeliveryNotes();
        } catch (error) {
            message.error(error.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
        } finally {
            setLoading(false);
        }
    };

    // Handle delete
    const handleDelete = async (id) => {
        try {
            await deleteDeliveryNote(id);
            message.success('ลบใบส่งของเรียบร้อยแล้ว');
            loadDeliveryNotes();
        } catch (error) {
            message.error(error.message || 'เกิดข้อผิดพลาดในการลบข้อมูล');
        }
    };

    // Handle status update
    const handleStatusUpdate = async (deliveryNote, newStatus) => {
        try {
            await updateDeliveryNote(deliveryNote.id, { status: newStatus });
            message.success('อัปเดตสถานะเรียบร้อยแล้ว');
            loadDeliveryNotes();
            if (selectedDeliveryNote && selectedDeliveryNote.id === deliveryNote.id) {
                setSelectedDeliveryNote(prev => ({ ...prev, status: newStatus }));
            }
        } catch (error) {
            message.error('เกิดข้อผิดพลาดในการอัปเดตสถานะ');
        }
    };

    // Print delivery note function (placeholder - you'll need to create PDF service)
    const handlePrintDeliveryNote = async (record) => {
        try {
            message.loading('กำลังสร้าง PDF...', 1);

            const response = await getDeliveryNoteById(record.id);
            console.log('Printing Delivery Note...', response.data);

            if (response.success) {
                const deliveryNoteData = response.data;

                // แปลงข้อมูลให้เข้ากับ format ของ PDF service
                const formattedDeliveryNote = {
                    deliveryNoteNumber: deliveryNoteData.delivery_note_number,
                    issueDate: deliveryNoteData.issue_date,
                    dueDate: deliveryNoteData.due_date,
                    document_date: deliveryNoteData.document_date,
                    customer: {
                        name: deliveryNoteData.customer_name,
                        address: deliveryNoteData.customer_address || '',
                        phone: deliveryNoteData.customer_phone || '',
                        tax_id: deliveryNoteData.customer_tax_id || ''
                    },
                    items: deliveryNoteData.items?.map(item => ({
                        product_name: item.product_name,
                        name: item.product_name,
                        description: item.description,
                        quantity: item.quantity,
                        unit: item.unit || 'ชิ้น',
                        unitPrice: item.unit_price,
                        is_free: item.is_free
                    })) || [],
                    notes: deliveryNoteData.notes,
                    status: deliveryNoteData.status,
                    vatType: deliveryNoteData.vat_type,
                    discountPercent: deliveryNoteData.discount_percent || 0,
                    data: deliveryNoteData
                };

                setPdfPreviewDeliveryNote(formattedDeliveryNote);
                setShowPdfTotals(false);
                setPdfModalVisible(true);

            } else {
                message.error('ไม่สามารถโหลดข้อมูลใบส่งของชั่วคราวได้');
            }
        } catch (error) {
            console.error('PDF generation error:', error);
            message.error('เกิดข้อผิดพลาดในการสร้าง PDF: ' + error.message);
        }
    };

    // Mobile Card Component
    const DeliveryNoteCard = ({ deliveryNote }) => (
        <Card
            size="small"
            style={{ marginBottom: 16 }}
            actions={[
                <Tooltip title="ดูรายละเอียด">
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => showDetailDrawer(deliveryNote)}
                        size="small"
                    />
                </Tooltip>,
                <Tooltip title="พิมพ์ PDF">
                    <Button
                        type="text"
                        icon={<PrinterOutlined />}
                        onClick={() => handlePrintDeliveryNote(deliveryNote)}
                        size="small"
                        style={{ color: '#722ed1' }}
                    />
                </Tooltip>,
                <Tooltip title="แก้ไข">
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => showEditModal(deliveryNote)}
                        size="small"
                    />
                </Tooltip>,
                <Popconfirm
                    title="คุณแน่ใจหรือไม่ที่จะลบใบส่งของนี้?"
                    onConfirm={() => handleDelete(deliveryNote.id)}
                    okText="ใช่"
                    cancelText="ไม่"
                >
                    <Tooltip title="ลบ">
                        <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            danger
                            size="small"
                        />
                    </Tooltip>
                </Popconfirm>
            ]}
        >
            <div onClick={() => showDetailDrawer(deliveryNote)} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <strong>{deliveryNote.delivery_note_number}</strong>
                    <Tag color={getStatusColor(deliveryNote.status)}>
                        {getStatusLabel(deliveryNote.status)}
                    </Tag>
                </div>

                <div style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{deliveryNote.contact_name}</div>
                    {deliveryNote.company_name && (
                        <div style={{ fontSize: '12px', color: '#666' }}>{deliveryNote.company_name}</div>
                    )}
                </div>

                <Row gutter={16} style={{ fontSize: '12px', color: '#666' }}>
                    <Col span={12}>
                        <CalendarOutlined style={{ marginRight: 4 }} />
                        {dayjs(deliveryNote.delivery_date).format('DD/MM/YYYY')}
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                        <DollarOutlined style={{ marginRight: 4 }} />
                        ฿{parseFloat(deliveryNote?.total_amount || 0)?.toLocaleString()}
                    </Col>
                </Row>

                {deliveryNote.expected_delivery_date && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                        กำหนดส่ง: {dayjs(deliveryNote.expected_delivery_date).format('DD/MM/YYYY')}
                    </div>
                )}
            </div>
        </Card>
    );

    const totalAmount = deliveryNotes.reduce((sum, item) => sum + parseFloat(item.total_amount), 0);
    const totalTaxAmount = deliveryNotes.reduce((sum, item) => sum + parseFloat(item.tax_amount), 0);

    // Table columns
    const columns = [
        {
            title: 'เลขที่ใบส่งของ',
            dataIndex: 'delivery_note_number',
            key: 'delivery_note_number',
            width: 150,
            render: (text, record) => (
                <Button
                    type="link"
                    onClick={() => showDetailDrawer(record)}
                    style={{ padding: 0, height: 'auto' }}
                >
                    {text}
                </Button>
            ),
        },
        {
            title: 'ลูกค้า',
            key: 'customer',
            width: 200,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>{record.contact_name}</div>
                    {record.company_name && (
                        <div style={{ fontSize: '12px', color: '#666' }}>{record.company_name}</div>
                    )}
                </div>
            ),
        },
        {
            title: 'วันที่เอกสาร',
            dataIndex: 'document_date',
            key: 'document_date',
            width: 120,
            render: (date) => dayjs(date).format('DD/MM/YYYY'),
        },
        {
            title: 'กำหนดส่ง',
            dataIndex: 'expected_delivery_date',
            key: 'expected_delivery_date',
            width: 120,
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'จำนวนเงิน',
            dataIndex: 'total_amount',
            key: 'total_amount',
            width: 120,
            align: 'right',
            render: (amount) => `฿${parseFloat(amount || 0).toLocaleString()}`,
        },
        {
            title: 'ภาษี',
            dataIndex: 'tax_amount',
            key: 'tax_amount',
            width: 120,
            align: 'right',
            render: (amount) => `฿${parseFloat(amount || 0).toLocaleString()}`,
        },
        {
            title: 'สถานะ',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {getStatusLabel(status)}
                </Tag>
            ),
        },
        {
            title: 'การดำเนินการ',
            key: 'actions',
            width: 200,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="ดูรายละเอียด">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => showDetailDrawer(record)}
                            size="small"
                        />
                    </Tooltip>

                    <Tooltip title="พิมพ์ PDF">
                        <Button
                            type="text"
                            icon={<PrinterOutlined />}
                            onClick={() => handlePrintDeliveryNote(record)}
                            size="small"
                            style={{ color: '#722ed1' }}
                        />
                    </Tooltip>

                    <Tooltip title="แก้ไข">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => showEditModal(record)}
                            size="small"
                        />
                    </Tooltip>

                    <Popconfirm
                        title="คุณแน่ใจหรือไม่ที่จะลบใบส่งของนี้?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="ใช่"
                        cancelText="ไม่"
                    >
                        <Tooltip title="ลบ">
                            <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                danger
                                size="small"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '24px' }}>
            <div style={{ marginBottom: '24px' }}>
                <Title level={2}>
                    <TruckOutlined style={{ marginRight: '8px' }} />
                    จัดการใบส่งของชั่วคราว
                </Title>
            </div>

            {/* Modal แสดง PDF */}
            <Modal
                title="ใบส่งสินค้าชั่วคราว"
                open={pdfModalVisible}
                onCancel={() => setPdfModalVisible(false)}
                footer={null}
                width={isMobile ? '100%' : 900}
                bodyStyle={{ height: isMobile ? '80vh' : '700px', padding: 0, display: 'flex', flexDirection: 'column' }}
                destroyOnClose
            >
                <div style={{ padding: '0 12px 8px' }}>
                    <Checkbox
                        checked={showPdfTotals}
                        onChange={event => setShowPdfTotals(event.target.checked)}
                    >
                        แสดงยอดรวมในเอกสาร
                    </Checkbox>
                </div>
                <SignatureLayoutControls
                    documentType="delivery_note"
                    value={signatureLayout}
                    onChange={setSignatureLayout}
                    onSave={handleSaveSignatureLayout}
                    saving={signatureLayoutSaving}
                />
                {pdfLoading ? <Spin size="large" style={{ margin: 'auto' }} /> : pdfDataUrl && (
                    <iframe
                        src={pdfDataUrl}
                        style={{ width: '100%', flex: 1, minHeight: 0, border: 'none' }}
                        title="ตัวอย่างใบส่งสินค้าชั่วคราว"
                    />
                )}
            </Modal>

            {/* Statistics Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                {statusCounts.map(item => (
                    <Col xs={12} sm={8} md={6} lg={4} key={item.status}>
                        <Card size={isMobile ? "small" : "default"}>
                            <Statistic
                                title={getStatusLabel(item.status)}
                                value={item.count}
                                valueStyle={{
                                    fontSize: isMobile ? '20px' : '24px',
                                    color: getStatusColor(item.status) === 'success' ? '#3f8600' :
                                        getStatusColor(item.status) === 'error' ? '#cf1322' :
                                            getStatusColor(item.status) === 'warning' ? '#d48806' : '#1890ff'
                                }}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Desktop Filters and Search */}
            {!isMobile && (
                <Card style={{ marginBottom: '24px' }}>
                    <Row gutter={16} align="middle">
                        <Col flex="300px">
                            <Input
                                placeholder="ค้นหาเลขที่ใบส่งของ หรือ ชื่อลูกค้า"
                                prefix={<SearchOutlined />}
                                value={filters.search}
                                onChange={(e) => handleSearch(e.target.value)}
                                allowClear
                            />
                        </Col>
                        <Col flex="200px">
                            <Select
                                placeholder="เลือกสถานะ"
                                value={filters.status}
                                onChange={(value) => handleFilterChange('status', value)}
                                allowClear
                                style={{ width: '100%' }}
                            >
                                {statusOptions.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col flex="200px">
                            <Select
                                placeholder="เลือกลูกค้า"
                                value={filters.contact_id}
                                onChange={(value) => handleFilterChange('contact_id', value)}
                                allowClear
                                showSearch
                                filterOption={customContactFilter}
                                style={{ width: '100%' }}
                            >
                                {contacts.map(contact => (
                                    <Option
                                        key={contact.id}
                                        value={contact.id}
                                        label={`${contact.name}${contact.company_name ? ` (${contact.company_name})` : ''}`}
                                    >
                                        {contact.name} {contact.company_name && `(${contact.company_name})`}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col>
                            <Space>
                                <Button onClick={clearFilters}>ล้างตัวกรอง</Button>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={showCreateModal}
                                >
                                    สร้างใบส่งของ
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>
            )}

            {/* Mobile Search and Filters */}
            {isMobile && (
                <>
                    <Card style={{ marginBottom: '16px' }}>
                        <Input
                            placeholder="ค้นหาเลขที่ใบส่งของ หรือ ชื่อลูกค้า"
                            prefix={<SearchOutlined />}
                            value={filters.search}
                            onChange={(e) => handleSearch(e.target.value)}
                            allowClear
                        />
                        <div style={{ marginTop: '12px', textAlign: 'center' }}>
                            <Button
                                icon={<FilterOutlined />}
                                onClick={() => setMobileFiltersVisible(true)}
                                style={{ marginRight: '8px' }}
                            >
                                ตัวกรอง
                            </Button>
                        </div>
                    </Card>

                    <Drawer
                        title="ตัวกรองข้อมูล"
                        placement="bottom"
                        height="auto"
                        open={mobileFiltersVisible}
                        onClose={() => setMobileFiltersVisible(false)}
                    >
                        <div style={{ padding: '16px 0' }}>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>สถานะ</label>
                                <Select
                                    placeholder="เลือกสถานะ"
                                    value={filters.status}
                                    onChange={(value) => handleFilterChange('status', value)}
                                    allowClear
                                    style={{ width: '100%' }}
                                >
                                    {statusOptions.map(option => (
                                        <Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>ลูกค้า</label>
                                <Select
                                    placeholder="เลือกลูกค้า"
                                    value={filters.contact_id}
                                    onChange={(value) => handleFilterChange('contact_id', value)}
                                    allowClear
                                    showSearch
                                    filterOption={customContactFilter}
                                    style={{ width: '100%' }}
                                >
                                    {contacts.map(contact => (
                                        <Option key={contact.id} value={contact.id}>
                                            {contact.name} {contact.company_name && `(${contact.company_name})`}
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            <div style={{ textAlign: 'center', paddingTop: '16px' }}>
                                <Space>
                                    <Button onClick={clearFilters}>ล้างตัวกรอง</Button>
                                    <Button
                                        type="primary"
                                        onClick={() => setMobileFiltersVisible(false)}
                                    >
                                        ตกลง
                                    </Button>
                                </Space>
                            </div>
                        </div>
                    </Drawer>
                </>
            )}

            {/* Data Display */}
            <Card>
                {isMobile ? (
                    <List
                        loading={loading}
                        dataSource={deliveryNotes}
                        renderItem={(deliveryNote) => <DeliveryNoteCard key={deliveryNote.id} deliveryNote={deliveryNote} />}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            onChange: (page, pageSize) => {
                                setPagination(prev => ({ ...prev, current: page, pageSize }));
                            },
                            showSizeChanger: false,
                            showQuickJumper: false,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} จาก ${total} รายการ`,
                            size: 'small'
                        }}
                    />
                ) : (
                    <Table
                        columns={columns}
                        dataSource={deliveryNotes}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} จาก ${total} รายการ`,
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 1200 }}
                        summary={() => (
                            <Table.Summary>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={3}>
                                        <strong></strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3}>
                                        <strong></strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3}>
                                        <strong></strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3}>
                                        <strong></strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4}>
                                        <strong>{`รวม  ${totalAmount.toLocaleString() || 0}`}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={5}>
                                        <strong>{`ภาษี  ${totalTaxAmount.toLocaleString() || 0}`}</strong>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />
                )}
            </Card>

            {/* Float Button for Mobile Add */}
            {isMobile && (
                <FloatButton
                    icon={<PlusOutlined />}
                    type="primary"
                    onClick={showCreateModal}
                    style={{ right: 24, bottom: 24 }}
                />
            )}

            {/* Create/Edit Form */}
            {isMobile ? (
                <Drawer
                    maskClosable={false}
                    title={editingDeliveryNote ? 'แก้ไขใบส่งของ' : 'สร้างใบส่งของใหม่'}
                    open={modalVisible}
                    onClose={() => setModalVisible(false)}
                    width="100%"
                    placement="right"
                    destroyOnClose
                    extra={
                        <Space>
                            <Button onClick={() => setModalVisible(false)}>ยกเลิก</Button>
                            <Button
                                type="primary"
                                onClick={() => form.submit()}
                                loading={loading}
                            >
                                บันทึก
                            </Button>
                        </Space>
                    }
                >
                    <DeliveryNoteForm
                        form={form}
                        onFinish={handleSubmit}
                        contacts={contacts}
                        products={products}
                        loading={loading}
                        onCancel={() => setModalVisible(false)}
                        isMobile={isMobile}
                        DeliveryNoteDetailInfo={DeliveryNoteDetailInfo}
                    />
                </Drawer>
            ) : (
                <Modal
                    maskClosable={false}
                    title={editingDeliveryNote ? 'แก้ไขใบส่งของ' : 'สร้างใบส่งของใหม่'}
                    open={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    footer={null}
                    width={900}
                    destroyOnClose
                >
                    <DeliveryNoteForm
                        form={form}
                        onFinish={handleSubmit}
                        contacts={contacts}
                        products={products}
                        loading={loading}
                        onCancel={() => setModalVisible(false)}
                        isMobile={isMobile}
                        DeliveryNoteDetailInfo={DeliveryNoteDetailInfo}
                    />
                </Modal>
            )}

            {/* Detail Drawer */}
            <Drawer
                title="รายละเอียดใบส่งของ"
                open={detailDrawerVisible}
                onClose={() => setDetailDrawerVisible(false)}
                width={isMobile ? "100%" : 600}
                extra={
                    selectedDeliveryNote && (
                        <Space>
                            <Select
                                value={selectedDeliveryNote.status}
                                onChange={(value) => handleStatusUpdate(selectedDeliveryNote, value)}
                                style={{ width: 150 }}
                            >
                                {statusOptions.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Space>
                    )
                }
            >
                {selectedDeliveryNote && <DeliveryNoteDetail deliveryNote={selectedDeliveryNote} isMobile={isMobile} />}
            </Drawer>

        </div>
    );
};

// Delivery Note Form Component พร้อม VAT Mode
const DeliveryNoteForm = ({ form, onFinish, contacts, products, loading, onCancel, isMobile, DeliveryNoteDetailInfo }) => {
    const [vatMode, setVatMode] = useState('include');
    const [calcValues, setCalcValues] = useState({
        subtotal: 0,
        discount: 0,
        taxAmount: 0,
        totalAmount: 0
    });

    // const unitOptions = [
    //     { value: 'กล่อง', label: 'กล่อง' },
    //     { value: 'เทสต์', label: 'เทสต์' },
    //     { value: 'ชิ้น', label: 'ชิ้น' },
    //     { value: 'ถุง', label: 'ถุง' },
    //     { value: 'แพ็ค', label: 'แพ็ค' },
    //     { value: 'เซ็ต', label: 'เซ็ต' },
    //     { value: 'คู่', label: 'คู่' },
    //     { value: 'ตัว', label: 'ตัว' },
    //     { value: 'กระป๋อง', label: 'กระป๋อง' },
    //     { value: 'ขวด', label: 'ขวด' },
    //     { value: 'โหล', label: 'โหล' },
    //     { value: 'กิโลกรัม', label: 'กิโลกรัม' },
    //     { value: 'กรัม', label: 'กรัม' },
    //     { value: 'ลิตร', label: 'ลิตร' },
    //     { value: 'มิลลิลิตร', label: 'มิลลิลิตร' },
    //     { value: 'เมตร', label: 'เมตร' },
    //     { value: 'เซนติเมตร', label: 'เซนติเมตร' },
    //     { value: 'ตารางเมตร', label: 'ตารางเมตร' },
    //     { value: 'ชั่วโมง', label: 'ชั่วโมง' },
    //     { value: 'วัน', label: 'วัน' },
    //     { value: 'เดือน', label: 'เดือน' },
    //     { value: 'ปี', label: 'ปี' },
    // ];

     // หน่วยนับมาตรฐาน - ตอนนี้ใช้ API แทน hardcoded array
    // หน่วยนับจะถูกโหลดจาก /api/units ผ่าน UnitsSelect component

    const getDetail = () => {
        setCalcValues({
            subtotal: DeliveryNoteDetailInfo.subtotal,
            discount: DeliveryNoteDetailInfo.discount,
            taxAmount: DeliveryNoteDetailInfo.tax_amount,
            totalAmount: DeliveryNoteDetailInfo.total_amount
        })
    }

    useEffect(() => {
        getDetail();
    }, [DeliveryNoteDetailInfo]);

    const customContactFilter = (input, option) => {
        if (!input) return true;
        let searchText = '';
        if (typeof option.children === 'string') {
            searchText = option.children;
        } else if (React.isValidElement(option.children)) {
            searchText = option.children.props?.children || '';
        } else if (Array.isArray(option.children)) {
            searchText = option.children.map(child =>
                typeof child === 'string' ? child : ''
            ).join(' ');
        }
        return searchText.toLowerCase().includes(input.toLowerCase());
    };

    const calculateTotals = (items, discount = 0, taxRate = 7, vatMode = 'include') => {
        const itemsSubtotal = items.reduce((sum, item) => {
            if (item?.is_free === true || item?.is_free === 1) {
                return sum;
            }
            return sum + ((item?.quantity || 0) * (item?.unit_price || 0));
        }, 0);

        let subtotal, taxAmount, totalAmount;

        if (vatMode === 'include') {
            const totalBeforeDiscount = itemsSubtotal;
            const discountAmount = discount || 0;
            totalAmount = totalBeforeDiscount - discountAmount;
            subtotal = totalAmount / (1 + (taxRate / 100));
            taxAmount = totalAmount - subtotal;
        } else {
            subtotal = itemsSubtotal;
            const discountAmount = discount || 0;
            const taxableAmount = subtotal - discountAmount;
            taxAmount = (taxableAmount * (taxRate || 0)) / 100;
            totalAmount = taxableAmount + taxAmount;
        }

        return {
            subtotal: Math.round(subtotal * 100) / 100,
            taxAmount: Math.round(taxAmount * 100) / 100,
            totalAmount: Math.round(totalAmount * 100) / 100,
            discountAmount: discount || 0
        };
    };

    const handleFormChange = () => {
        const items = form.getFieldValue('items') || [];
        const discount = form.getFieldValue('discount') || 0;
        const taxRate = form.getFieldValue('tax_rate') || 7;
        const currentVatMode = form.getFieldValue('vat_mode') || 'include';

        setVatMode(currentVatMode);
        const calculations = calculateTotals(items, discount, taxRate, currentVatMode);
        setCalcValues(calculations);
    };

    const handleFinish = (values) => {
        const calculations = calculateTotals(
            values.items || [],
            values.discount || 0,
            values.tax_rate || 7,
            values.vat_mode || 'include'
        );

        const enhancedValues = {
            ...values,
            subtotal: calcValues.subtotal,
            tax_amount: calcValues.taxAmount,
            total_amount: calcValues.totalAmount
        };

        onFinish(enhancedValues);
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            onValuesChange={handleFormChange}
            initialValues={{
                delivery_date: dayjs(),
                expected_delivery_date: dayjs().add(7, 'day'),
                tax_rate: 7,
                discount: 0,
                vat_mode: 'include',
                items: [{ description: '', quantity: 1, unit_price: 0, unit: 'กล่อง', is_free: false }]
            }}
        >
            <Row gutter={isMobile ? [0, 16] : 16}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name="contact_id"
                        label="ลูกค้า"
                        rules={[{ required: true, message: 'กรุณาเลือกลูกค้า' }]}
                    >
                        <Select
                            placeholder="เลือกลูกค้า"
                            showSearch
                            filterOption={customContactFilter}
                        >
                            {contacts.map(contact => (
                                <Option key={contact.id} value={contact.id}>
                                    {contact.name} {contact.company_name && `(${contact.company_name})`}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                    <Form.Item
                        name="document_date"
                        label="วันที่เอกสาร"
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>

                </Col>

            </Row>

            <Row gutter={isMobile ? [0, 16] : 16}>
                <Col xs={24} sm={6}>
                    <Form.Item
                        name="delivery_date"
                        label="วันที่ส่งของ"
                        rules={[{ required: true, message: 'กรุณาเลือกวันที่' }]}
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                    <Form.Item
                        name="expected_delivery_date"
                        label="กำหนดส่งของ"
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
            </Row>

            <Divider>รายการสินค้า</Divider>

            <Form.List name="items">
                {(fields, { add, remove }) => (
                    <>
                        {fields.map((field, index) => (
                            <Card
                                key={field.key}
                                size="small"
                                title={`รายการที่ ${index + 1}`}
                                extra={
                                    fields.length > 1 && (
                                        <Button
                                            type="link"
                                            danger
                                            onClick={() => remove(field.name)}
                                        >
                                            ลบ
                                        </Button>
                                    )
                                }
                                style={{ marginBottom: 16 }}
                            >
                                <Row gutter={isMobile ? [0, 16] : 16}>
                                    <Col xs={24} sm={8}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'product_id']}
                                            label="สินค้า"
                                        >
                                            <Select
                                                placeholder="เลือกสินค้า (ไม่บังคับ)"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) => {
                                                    if (!input) return true;
                                                    const searchText = typeof option.children === 'string'
                                                        ? option.children
                                                        : '';
                                                    return searchText.toLowerCase().includes(input.toLowerCase());
                                                }}
                                                onChange={(value, option) => {
                                                    if (value && option) {
                                                        const currentItems = form.getFieldValue('items');
                                                        currentItems[index] = {
                                                            ...currentItems[index],
                                                            product_id: value,
                                                            // description: option.children,
                                                            unit: option.unit,
                                                            unit_price: products.find(p => p.id === value)?.price || 0
                                                        };
                                                        form.setFieldsValue({ items: currentItems });
                                                        handleFormChange();
                                                    }
                                                }}
                                            >
                                                {products.map(product => (
                                                    <Option key={product.id} value={product.id}>
                                                        {product.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={16}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'is_free']}
                                            valuePropName="checked"
                                            initialValue={false}
                                        >
                                            <div style={{ paddingTop: isMobile ? 0 : 30 }}>
                                                <Checkbox
                                                    danger
                                                    onChange={(e) => {
                                                        const currentItems = form.getFieldValue('items');
                                                        currentItems[index].is_free = e.target.checked;
                                                        form.setFieldsValue({ items: currentItems });
                                                        handleFormChange();
                                                    }}
                                                    checked={form.getFieldValue('items')?.[index]?.is_free || false}
                                                >
                                                    <span style={{ color: 'red' }}>ไม่ต้องแสดงราคา !</span>
                                                </Checkbox>
                                            </div>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={16}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'description']}
                                            label="รายละเอียด"
                                            rules={[{ required: false, message: 'กรุณากรอกรายละเอียด' }]}
                                        >
                                            <Input placeholder="รายละเอียดสินค้า/บริการ" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={isMobile ? [0, 16] : 16}>
                                    <Col xs={12} sm={6}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'quantity']}
                                            label="จำนวน"
                                            rules={[{ required: true, message: 'กรุณากรอกจำนวน' }]}
                                        >
                                            <InputNumber
                                                min={0.01}
                                                step={0.01}
                                                style={{ width: '100%' }}
                                                placeholder="จำนวน"
                                                onChange={handleFormChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'unit']}
                                            label="หน่วยนับ"
                                            rules={[{ required: true, message: 'กรุณาเลือกหน่วยนับ' }]}
                                        >
                                            <UnitsSelect
                                                placeholder="เลือกหน่วยนับ"
                                                showSearch
                                                allowClear
                                                showRefresh
                                                valueField="unit_name"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'unit_price']}
                                            label="ราคาต่อหน่วย"
                                            rules={[{ required: true, message: 'กรุณากรอกราคา' }]}
                                        >
                                            <InputNumber
                                                min={0}
                                                step={0.01}
                                                style={{ width: '100%' }}
                                                placeholder="ราคาต่อหน่วย"
                                                formatter={value => `฿ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={value => value.replace(/฿\s?|(,*)/g, '')}
                                                onChange={handleFormChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Form.Item label="รวม">
                                            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => {
                                                const prevItems = prevValues.items || [];
                                                const currentItems = currentValues.items || [];
                                                return JSON.stringify(prevItems) !== JSON.stringify(currentItems);
                                            }}>
                                                {({ getFieldValue }) => {
                                                    const items = getFieldValue('items') || [];
                                                    const item = items[index] || {};
                                                    const total = item.is_free === true ? 0 : (item.quantity || 0) * (item.unit_price || 0);
                                                    return (
                                                        <InputNumber
                                                            value={total}
                                                            disabled
                                                            style={{
                                                                width: '100%',
                                                                color: item.is_free === true ? '#52c41a' : undefined
                                                            }}
                                                            formatter={value => `฿ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                        />
                                                    );
                                                }}
                                            </Form.Item>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        ))}
                        <Button
                            type="dashed"
                            onClick={() => add({ description: '', quantity: 1, unit_price: 0, unit: 'ชิ้น', is_free: false })}
                            block
                            icon={<PlusOutlined />}
                        >
                            เพิ่มรายการ
                        </Button>
                    </>
                )}
            </Form.List>

            <Divider>การคำนวณภาษี</Divider>

            <Row gutter={isMobile ? [0, 16] : 16}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name="vat_mode"
                        defaultActiveKey="include"
                        label={
                            <span>
                                <CalculatorOutlined style={{ marginRight: 8 }} />
                                โหมดการคำนวณภาษี
                            </span>
                        }
                        rules={[{ required: true, message: 'กรุณาเลือกโหมดการคำนวณภาษี' }]}
                    >
                        <Radio.Group onChange={handleFormChange}>
                            <Radio.Button value="exclude">VAT EXCLUDE (ยังไม่รวมภาษี)</Radio.Button>
                            <Radio.Button value="include">VAT INCLUDE (รวมภาษีแล้ว)</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name="tax_rate"
                        label="อัตราภาษี (%)"
                    >
                        <InputNumber
                            min={0}
                            max={100}
                            step={0.01}
                            style={{ width: '100%' }}
                            onChange={handleFormChange}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Alert
                message={
                    vatMode === 'include'
                        ? "โหมด VAT INCLUDE: ราคาสินค้าที่ป้อนรวมภาษีแล้ว ระบบจะแยกคำนวณภาษีออกจากยอดรวม"
                        : "โหมด VAT EXCLUDE: ราคาสินค้าที่ป้อนยังไม่รวมภาษี ระบบจะคำนวณภาษีเพิ่มเติม"
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            <Row gutter={isMobile ? [0, 16] : 16}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name="discount"
                        label="ส่วนลด"
                    >
                        <InputNumber
                            min={0}
                            step={0.01}
                            style={{ width: '100%' }}
                            formatter={value => `฿ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/฿\s?|(,*)/g, '')}
                            onChange={handleFormChange}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Card title="สรุปการคำนวณ" style={{ marginBottom: 16 }}>
                <Row>
                    <Col span={12}><strong>ยอดก่อนภาษี:</strong></Col>
                    <Col span={12} style={{ textAlign: 'right' }}>{calcValues.subtotal?.toLocaleString()}</Col>
                </Row>
                {calcValues.discountAmount > 0 && (
                    <Row>
                        <Col span={12}><strong>ส่วนลด:</strong></Col>
                        <Col span={12} style={{ textAlign: 'right' }}>-{calcValues.discountAmount?.toLocaleString()}</Col>
                    </Row>
                )}
                <Row>
                    <Col span={12}><strong>ภาษีมูลค่าเพิ่ม:</strong></Col>
                    <Col span={12} style={{ textAlign: 'right' }}>{calcValues.taxAmount?.toLocaleString()}</Col>
                </Row>
                <Divider style={{ margin: '8px 0' }} />
                <Row>
                    <Col span={12}><strong style={{ color: '#1890ff' }}>ยอดรวมทั้งสิ้น:</strong></Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                        <strong style={{ color: '#1890ff' }}>{calcValues.totalAmount?.toLocaleString()}</strong>
                    </Col>
                </Row>
            </Card>

            <Form.Item
                name="notes"
                label="หมายเหตุ"
            >
                <TextArea rows={3} placeholder="หมายเหตุเพิ่มเติม" />
            </Form.Item>

            {!isMobile && (
                <Form.Item>
                    <Space>
                        <Button onClick={onCancel}>
                            ยกเลิก
                        </Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            บันทึก
                        </Button>
                    </Space>
                </Form.Item>
            )}
        </Form>
    );
};

// Delivery Note Detail Component
const DeliveryNoteDetail = ({ deliveryNote, isMobile }) => {
    const statusOptions = [
        { value: 'draft', label: 'ร่าง', color: 'default' },
        { value: 'sent', label: 'ส่งแล้ว', color: 'processing' },
        { value: 'received', label: 'ลูกค้ารับแล้ว', color: 'success' },
        { value: 'cancelled', label: 'ยกเลิก', color: 'error' },
    ];

    const getStatusColor = (status) => {
        const statusConfig = statusOptions.find(opt => opt.value === status);
        return statusConfig?.color || 'default';
    };

    const getStatusLabel = (status) => {
        const statusConfig = statusOptions.find(opt => opt.value === status);
        return statusConfig?.label || status;
    };

    return (
        <Tabs defaultActiveKey="1">
            <TabPane tab="ข้อมูลใบส่งของ" key="1">
                <Descriptions column={isMobile ? 1 : 2} bordered size={isMobile ? "small" : "default"}>
                    <Descriptions.Item label="เลขที่ใบส่งของ" span={isMobile ? 1 : 2}>
                        {deliveryNote.delivery_note_number}
                    </Descriptions.Item>
                    <Descriptions.Item label="ลูกค้า" span={isMobile ? 1 : 2}>
                        <div>
                            <div>{deliveryNote.contact_name}</div>
                            {deliveryNote.company_name && (
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                    {deliveryNote.company_name}
                                </div>
                            )}
                        </div>
                    </Descriptions.Item>
                    <Descriptions.Item label="วันที่ส่งของ">
                        {dayjs(deliveryNote.delivery_date).format('DD/MM/YYYY')}
                    </Descriptions.Item>
                    <Descriptions.Item label="กำหนดส่ง">
                        {deliveryNote.expected_delivery_date ? dayjs(deliveryNote.expected_delivery_date).format('DD/MM/YYYY') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="สถานะ" span={isMobile ? 1 : 2}>
                        <Tag color={getStatusColor(deliveryNote.status)}>
                            {getStatusLabel(deliveryNote.status)}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="โหมดการคำนวณภาษี" span={isMobile ? 1 : 2}>
                        <Tag color={deliveryNote.vat_mode === 'include' ? 'green' : 'blue'}>
                            {deliveryNote.vat_mode === 'include' ? 'VAT INCLUDE (รวมภาษีแล้ว)' : 'VAT EXCLUDE (ยังไม่รวมภาษี)'}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="ยอดรวมก่อนภาษี">
                        ฿{parseFloat(deliveryNote.subtotal)?.toLocaleString()}
                    </Descriptions.Item>
                    <Descriptions.Item label="ส่วนลด">
                        ฿{parseFloat(deliveryNote.discount || 0)?.toLocaleString()}
                    </Descriptions.Item>
                    <Descriptions.Item label={`ภาษี (${deliveryNote.tax_rate || 0}%)`}>
                        ฿{parseFloat(deliveryNote.tax_amount)?.toLocaleString()}
                    </Descriptions.Item>
                    <Descriptions.Item label="ยอดรวมทั้งสิ้น">
                        <strong>฿{parseFloat(deliveryNote.total_amount)?.toLocaleString()}</strong>
                    </Descriptions.Item>
                    {deliveryNote.notes && (
                        <Descriptions.Item label="หมายเหตุ" span={isMobile ? 1 : 2}>
                            {deliveryNote.notes}
                        </Descriptions.Item>
                    )}
                    <Descriptions.Item label="ผู้สร้าง">
                        {deliveryNote.created_by_name}
                    </Descriptions.Item>
                    <Descriptions.Item label="วันที่สร้าง">
                        {dayjs(deliveryNote.created_at).format('DD/MM/YYYY HH:mm')}
                    </Descriptions.Item>
                </Descriptions>
            </TabPane>

            <TabPane tab="รายการสินค้า" key="2">
                {deliveryNote.items && deliveryNote.items.length > 0 ? (
                    isMobile ? (
                        <List
                            dataSource={deliveryNote.items}
                            renderItem={(item, index) => (
                                <Card
                                    size="small"
                                    style={{ marginBottom: 8 }}
                                    title={`รายการที่ ${index + 1}`}
                                    extra={item.is_free ? <Tag color="red">ไม่แสดงราคา</Tag> : null}
                                >
                                    <p><strong>รายการ:</strong> {item.description}</p>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <p><strong>จำนวน:</strong> {item.quantity} {item.unit || 'ชิ้น'}</p>
                                        </Col>
                                        <Col span={12}>
                                            <p><strong>ราคา/หน่วย:</strong> ฿{parseFloat(item.unit_price)?.toLocaleString()}</p>
                                        </Col>
                                        <Col span={12}>
                                            <p><strong>รวม:</strong> ฿{parseFloat(item.total_price)?.toLocaleString()}</p>
                                        </Col>
                                    </Row>
                                </Card>
                            )}
                        />
                    ) : (
                        <Table
                            dataSource={deliveryNote.items}
                            rowKey="id"
                            pagination={false}
                            columns={[
                                {
                                    title: 'รายการ',
                                    dataIndex: 'description',
                                    key: 'description',
                                },
                                {
                                    title: 'จำนวน',
                                    key: 'quantity',
                                    align: 'center',
                                    width: 120,
                                    render: (_, record) => `${record.quantity} ${record.unit || 'ชิ้น'}`,
                                },
                                {
                                    title: 'ราคาต่อหน่วย',
                                    dataIndex: 'unit_price',
                                    key: 'unit_price',
                                    align: 'right',
                                    width: 120,
                                    render: (price) => `฿${parseFloat(price)?.toLocaleString()}`,
                                },
                                {
                                    title: 'รวม',
                                    dataIndex: 'total_price',
                                    key: 'total_price',
                                    align: 'right',
                                    width: 120,
                                    render: (total) => `฿${parseFloat(total)?.toLocaleString()}`,
                                },
                                {
                                    title: 'การแสดงผล',
                                    dataIndex: 'is_free',
                                    key: 'is_free',
                                    align: 'center',
                                    width: 100,
                                    render: (is_free) => is_free ? <Tag color="green">ไม่ต้องแสดงราคาในเอกสาร</Tag> : '-',
                                },
                            ]}
                        />
                    )
                ) : (
                    <Empty description="ไม่มีรายการสินค้า" />
                )}
            </TabPane>
        </Tabs>
    );
};

export default DeliveryNotesPage;
